import { ref } from 'vue'
import axios from 'axios'
import store from '@/store'
import api from '@/store/api/donor'
import type {
  RecurringBackfillPreview,
  RecurringDonationRestartInput,
  RecurringDonationSeries,
  RecurringDonationSeriesInput,
  RecurringDonationSeriesUpdate,
} from '@/types/recurringDonation'
import { mapRecurringSeries } from '@/helpers/recurring/mapSeries'
import { recurringDonationsEnabled } from '@/helpers/recurringDonationsEnabled'
import { refreshManualDonations } from '@/composables/useManualGivingDonations'

function canFetchRecurringDonations(): boolean {
  if (!recurringDonationsEnabled()) return false
  if (import.meta.env.MODE === 'mockapi') return true
  return store.getters.hasFullGivingRecord === true
}

const recurringSeries = ref<RecurringDonationSeries[]>([])
const isReady = ref(false)
const isLoading = ref(false)
let loadPromise: Promise<void> | null = null
let givingRecordWatchStarted = false

function unwrapSeries(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== 'object') return null
  const obj = data as Record<string, unknown>
  if (obj.series && typeof obj.series === 'object') {
    return obj.series as Record<string, unknown>
  }
  return obj
}

function upsertSeries(entry: RecurringDonationSeries): void {
  recurringSeries.value = [
    entry,
    ...recurringSeries.value.filter((row) => row.id !== entry.id),
  ]
}

function startGivingRecordWatch() {
  if (givingRecordWatchStarted) return
  givingRecordWatchStarted = true

  store.watch(
    (_state, getters) => getters.hasFullGivingRecord === true,
    (enabled) => {
      if (import.meta.env.MODE === 'mockapi') {
        if (!isReady.value && !isLoading.value) {
          void loadRecurringDonations()
        }
        return
      }
      if (enabled) {
        if (!isReady.value && !isLoading.value) {
          void loadRecurringDonations()
        }
        return
      }
      recurringSeries.value = []
      isReady.value = false
      loadPromise = null
    },
    { immediate: true }
  )
}

// Loads once per session; the plan watch above clears state when the giving
// record is locked again, and every mutation upserts its own response.
async function loadRecurringDonations(): Promise<void> {
  if (!canFetchRecurringDonations()) {
    recurringSeries.value = []
    return
  }
  if (isReady.value) return
  if (loadPromise) return loadPromise

  const run = async () => {
    isLoading.value = true
    try {
      const response = await api.fetchRecurringDonations()
      const rows = (response.data?.series ?? []) as Record<string, unknown>[]
      recurringSeries.value = rows.map(mapRecurringSeries)
      isReady.value = true
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        recurringSeries.value = []
        isReady.value = true
        return
      }
      console.error('Failed to load recurring donations', error)
      isReady.value = true
      throw error
    } finally {
      isLoading.value = false
      loadPromise = null
    }
  }

  loadPromise = run()
  return loadPromise
}

function seriesPayload(
  input: RecurringDonationSeriesInput
): Record<string, unknown> {
  return {
    charity: input.charity.trim(),
    amountPence: input.amountPence,
    giftAidDeclared: input.giftAidDeclared,
    notes: input.notes?.trim() || undefined,
    frequency: input.frequency,
    firstDonationDate: input.firstDonationDate,
    endCondition: input.endCondition,
    confirmLargeBackfill: input.confirmLargeBackfill === true,
  }
}

export function useRecurringDonations() {
  startGivingRecordWatch()

  if (!isReady.value && !isLoading.value && !loadPromise) {
    void loadRecurringDonations()
  }

  const previewRecurringDonation = async (
    input: RecurringDonationSeriesInput
  ): Promise<RecurringBackfillPreview> => {
    const response = await api.previewRecurringDonation(seriesPayload(input))
    return response.data as RecurringBackfillPreview
  }

  const addRecurringDonation = async (
    input: RecurringDonationSeriesInput
  ): Promise<
    | { series: RecurringDonationSeries }
    | { confirmation: RecurringBackfillPreview }
    | { limitExceeded: RecurringBackfillPreview }
  > => {
    const response = await api.postRecurringDonation(seriesPayload(input))
    if (response.status === 409) {
      return { confirmation: response.data.preview as RecurringBackfillPreview }
    }
    if (response.status === 422) {
      return {
        limitExceeded: response.data.preview as RecurringBackfillPreview,
      }
    }
    const raw = unwrapSeries(response.data)
    if (!raw) throw new Error('Recurring donation response was empty')
    const entry = mapRecurringSeries(raw)
    upsertSeries(entry)
    isReady.value = true
    await refreshManualDonations()
    return { series: entry }
  }

  const updateRecurringDonation = async (
    id: string,
    input: RecurringDonationSeriesUpdate
  ): Promise<RecurringDonationSeries> => {
    const response = await api.putRecurringDonation(id, {
      amountPence: input.amountPence,
      giftAidDeclared: input.giftAidDeclared,
      notes: input.notes,
      frequency: input.frequency,
      nextDonationDate: input.nextDonationDate,
      endCondition: input.endCondition,
      applyScope: input.applyScope,
    })
    const raw = unwrapSeries(response.data)
    if (!raw) throw new Error('Recurring donation response was empty')
    const entry = mapRecurringSeries(raw)
    upsertSeries(entry)
    await refreshManualDonations()
    return entry
  }

  const stopRecurringDonation = async (
    id: string
  ): Promise<RecurringDonationSeries> => {
    const response = await api.stopRecurringDonation(id)
    const raw = unwrapSeries(response.data)
    if (!raw) throw new Error('Recurring donation response was empty')
    const entry = mapRecurringSeries(raw)
    upsertSeries(entry)
    return entry
  }

  const restartRecurringDonation = async (
    id: string,
    input: RecurringDonationRestartInput
  ): Promise<RecurringDonationSeries> => {
    const response = await api.restartRecurringDonation(id, {
      amountPence: input.amountPence,
      giftAidDeclared: input.giftAidDeclared,
      notes: input.notes,
      frequency: input.frequency,
      nextDonationDate: input.nextDonationDate,
      endCondition: input.endCondition,
    })
    const raw = unwrapSeries(response.data)
    if (!raw) throw new Error('Recurring donation response was empty')
    const entry = mapRecurringSeries(raw)
    upsertSeries(entry)
    await refreshManualDonations()
    return entry
  }

  const skipRecurringNextOccurrence = async (
    id: string
  ): Promise<RecurringDonationSeries> => {
    const response = await api.skipRecurringNextOccurrence(id)
    const raw = unwrapSeries(response.data)
    if (!raw) throw new Error('Recurring donation response was empty')
    const entry = mapRecurringSeries(raw)
    upsertSeries(entry)
    return entry
  }

  const deleteRecurringDonation = async (id: string): Promise<void> => {
    await api.deleteRecurringDonation(id)
    recurringSeries.value = recurringSeries.value.filter((row) => row.id !== id)
    await refreshManualDonations()
  }

  const seriesById = (
    id: string | undefined
  ): RecurringDonationSeries | null => {
    if (!id) return null
    return recurringSeries.value.find((row) => row.id === id) ?? null
  }

  return {
    recurringSeries,
    isReady,
    isLoading,
    previewRecurringDonation,
    addRecurringDonation,
    updateRecurringDonation,
    stopRecurringDonation,
    restartRecurringDonation,
    skipRecurringNextOccurrence,
    deleteRecurringDonation,
    seriesById,
  }
}
